import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { ApiCampaignsService } from '../../data/services/api/api-campaigns.service';
import { CampaignService } from '../../data/services/view/campaign.service';
import { STATUS_INFO, CAMPAIGN_STATUS_ENUM, CAMPAIGN_STATUS_COLOR_ENUM } from '../../data/constants/campaigns.constants';
import { Location } from '@angular/common';

@Component({
  selector: 'app-campaign-detail',
  templateUrl: './campaign-detail.component.html',
  styleUrls: ['./campaign-detail.component.scss']
})
export class CampaignDetailComponent implements OnInit, OnDestroy {
  campaign: any = null;
  isLoading = true;
  hasError = false;
  errorMessage = '';
  statusInfo = STATUS_INFO;
  statusEnum = CAMPAIGN_STATUS_ENUM;
  statusColorEnum = CAMPAIGN_STATUS_COLOR_ENUM;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private location: Location,
    private apiCampaignsService: ApiCampaignsService,
    public campaignService: CampaignService
  ) {}

  ngOnInit(): void {
    const campaignId = this.route.snapshot.paramMap.get('id');
    if (campaignId) {
      this.loadCampaignDetail(campaignId);
    } else {
      this.hasError = true;
      this.errorMessage = 'ID de campaña no proporcionado';
      this.isLoading = false;
    }
  }

  ngOnDestroy(): void {
    this.campaign = null;
  }

  loadCampaignDetail(id: string): void {
    this.isLoading = true;
    this.hasError = false;

    this.apiCampaignsService.getById(id).subscribe({
      next: (response) => {
        if (!response.error && response.data) {
          this.campaign = response.data;
          this.isLoading = false;
        } else {
          this.hasError = true;
          this.errorMessage = response.msg || 'Error al cargar el detalle de la campaña';
          this.isLoading = false;
        }
      },
      error: (error) => {
        this.hasError = true;
        this.errorMessage = 'Error de conexión al cargar la campaña';
        this.isLoading = false;
        console.error('Error loading campaign detail:', error);
      }
    });
  }

  goBack(): void {
    this.location.back();
  }

  getStatusInfo(status: string) {
    return this.statusInfo[status as keyof typeof STATUS_INFO] || this.statusInfo.PENDING;
  }

  getStatusLabel(status: string): string {
    return this.statusEnum[status as keyof typeof this.statusEnum] || status;
  }

  getStatusColor(status: string): string {
    return this.statusColorEnum[status as keyof typeof this.statusColorEnum] || 'warning';
  }

  formatDate(date: Date | string | undefined): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  }

  formatDateShort(date: Date | string | undefined): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  }

  getChannelsList(): string {
    if (!this.campaign?.channels || this.campaign.channels.length === 0) {
      return 'Ninguno';
    }
    return this.campaign.channels.join(', ');
  }

  getChannelIcon(channel: string): string {
    const icons: { [key: string]: string } = {
      'EMAIL': 'fa-solid fa-envelope',
      'SMS': 'fa-solid fa-comment-sms',
      'WHATSAPP': 'fa-brands fa-whatsapp',
      'CALL': 'fa-solid fa-phone'
    };
    return icons[channel] || 'fa-solid fa-paper-plane';
  }

  getResultIcon(success: boolean): string {
    return success ? 'fa-solid fa-circle-check' : 'fa-solid fa-circle-xmark';
  }

  getResultColor(success: boolean): string {
    return success ? '#22c55e' : '#ef4444';
  }

  /**
   * Calcula la hora de envío real de un canal basándose en scheduledTime
   * 
   * @param channelName - Nombre del canal (email, sms, whatsapp, call)
   * @returns Hora en formato HH:mm o null si no se puede calcular
   */
  getChannelExecutionTime(channelName: 'email' | 'sms' | 'whatsapp' | 'call'): string | null {
    if (!this.campaign?.strategyId?.channels) return null;

    const channel = this.campaign.strategyId.channels[channelName];
    if (!channel) return null;

    // Todos los canales ahora tienen scheduledTime
    return channel.scheduledTime || null;
  }

  /**
   * Obtiene la hora programada del primer canal habilitado (menor prioridad)
   */
  private getBaseScheduledTime(): string | null {
    if (!this.campaign?.strategyId?.channels) return null;

    const channels = this.campaign.strategyId.channels;
    const allChannels = [
      { name: 'email', config: channels.email },
      { name: 'sms', config: channels.sms },
      { name: 'whatsapp', config: channels.whatsapp },
      { name: 'call', config: channels.call }
    ];

    // Filtrar habilitados y ordenar por prioridad
    const enabledChannels = allChannels
      .filter(ch => ch.config?.enabled)
      .sort((a, b) => (a.config?.priority || 999) - (b.config?.priority || 999));

    // Retornar scheduledTime del primer canal (menor prioridad)
    const firstChannel = enabledChannels[0];
    return firstChannel?.config?.scheduledTime || null;
  }

  /**
   * Suma minutos a una hora en formato HH:mm
   * @param time - Hora base en formato HH:mm (ej: "15:00")
   * @param minutes - Minutos a sumar
   * @returns Nueva hora en formato HH:mm
   */
  private addMinutesToTime(time: string, minutes: number): string {
    const [hours, mins] = time.split(':').map(Number);
    const date = new Date();
    date.setHours(hours, mins, 0, 0);
    date.setMinutes(date.getMinutes() + minutes);

    const newHours = date.getHours().toString().padStart(2, '0');
    const newMins = date.getMinutes().toString().padStart(2, '0');
    return `${newHours}:${newMins}`;
  }
}
